/**
 * Admin - Detalhe da empresa: cabecalho com logo e saude, seis abas e edicao.
 *
 * Decisoes nao obvias:
 *  - O id da URL e conferido contra `useCompany().companies` ANTES de buscar:
 *    id desconhecido mostra o vazio na hora, sem requisicao e sem esqueleto.
 *  - `key={company.id}` no bloco carregado: trocar de empresa (pela URL)
 *    descarta aba, edicao e convites locais da anterior, em vez de herdar.
 *  - A ABA mora na URL (`emp.aba`): "O que fazer agora" da lista e os achados
 *    de saude abrem a empresa direto na aba onde se resolve, e o link pode ser
 *    mandado para outra pessoa da equipe.
 *  - As seis abas ficam MONTADAS e as inativas recebem `hidden`: Ofertas e
 *    Equipe tem estado local (interruptores, convites) que sumiria se a aba
 *    fosse desmontada ao dar uma olhada no Resumo.
 *  - Edicao basica: nome, segmento e status vao pelo `updateCompany` do
 *    contexto (valem em todas as telas da previa: cabecalho, lista, seletor);
 *    o contato, que o contexto ainda nao guarda, fica numa camada local.
 *  - A saude e derivada aqui (saude.ts) do detalhe carregado e alimenta o
 *    selo do cabecalho, o contador da aba Integracoes e o cartao do Resumo.
 *  - Os convites da aba Equipe moram AQUI: a vendedora convidada entra na
 *    contagem da aba e no passo "vendedora cadastrada" da saude na hora.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EmptyState, ErrorPanel, RefreshingDot, SimulatedNotice } from '../../../components/PageState';
import { HeroBanner, LinkButton, Tabs, type TabItem } from '../../../components/ui';
import { useApiResource } from '../../../lib/api';
import { useCompany } from '../../../lib/companyContext';
import { formatCount, formatDate } from '../../../lib/format';
import { describeSelection } from '../../../lib/period';
import { usePeriod } from '../../../lib/periodContext';
import type { CompanySummary, TeamMember } from '../../../types';
import { fetchCompanyDetail } from '../api';
import { AbaComissao } from '../components/AbaComissao';
import { AbaEquipe } from '../components/AbaEquipe';
import { AbaHistorico } from '../components/AbaHistorico';
import { AbaOfertas } from '../components/AbaOfertas';
import { AbaResumo } from '../components/AbaResumo';
import { CabecalhoEmpresa } from '../components/CabecalhoEmpresa';
import { ModalEditarEmpresa, type EdicaoBasica } from '../components/ModalEditarEmpresa';
import { PainelIntegracoes } from '../components/PainelIntegracoes';
import { iniciaisDe } from '../identidade';
import { semConsulta } from '../lista';
import { agoraDaTela, diagnosticar } from '../saude';
import type { AbaDetalhe } from '../types';
import { ABAS_DETALHE, ehAbaDetalhe } from '../vocabulario';

const SALVO_VISIVEL_MS = 2_400;
const PARAM_ABA = 'emp.aba';

function painelId(aba: AbaDetalhe): string {
  return `painel-empresa-${aba}`;
}

/** Volta para a lista preservando o periodo e o recorte, sem a aba do detalhe. */
function linkDaLista(search: string): string {
  const p = new URLSearchParams(search);
  p.delete(PARAM_ABA);
  const texto = p.toString();
  return `/admin/empresas${texto === '' ? '' : `?${texto}`}`;
}

function Esqueleto(): JSX.Element {
  return (
    <>
      <p role="status" className="sr-only">
        Carregando os dados da empresa
      </p>
      <div aria-hidden="true" className="flex flex-col gap-6">
        <div className="flex gap-2">
          {ABAS_DETALHE.map((aba) => (
            <span key={aba.value} className="h-11 w-28 animate-pulse rounded-xl border border-line bg-surface" />
          ))}
        </div>
        <div className="h-40 animate-pulse rounded-2xl border border-line bg-surface" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {ABAS_DETALHE.slice(0, 4).map((aba) => (
            <span key={aba.value} className="h-28 animate-pulse rounded-2xl border border-line bg-surface" />
          ))}
        </div>
        <div className="h-72 animate-pulse rounded-2xl border border-line bg-surface" />
      </div>
    </>
  );
}

export function AdminEmpresaDetalhe(): JSX.Element {
  const { companyId } = useParams<{ companyId: string }>();
  const { companies } = useCompany();
  const location = useLocation();
  const id = companyId ?? '';
  const conhecida = companies.find((c) => c.id === id) ?? null;

  useEffect(() => {
    document.title = `${conhecida === null ? 'Empresa não encontrada' : conhecida.name} — Administração · Converte Fácil`;
  }, [conhecida]);

  if (conhecida === null) {
    return (
      <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
        <HeroBanner
          icon="empresa"
          title="Empresa não encontrada"
          backTo={
            <LinkButton to={linkDaLista(location.search)} variant="ghost" icon="seta-esq" keepSearch={false}>
              <span className="sr-only">Voltar para a lista de empresas</span>
            </LinkButton>
          }
          description="O identificador da URL não corresponde a nenhuma empresa cadastrada."
        />
        <EmptyState
          title="Nenhuma empresa com este identificador"
          description={
            <>
              Confira o endereço ou volte para a lista. Identificador recebido: <code className="font-mono text-xs text-ink">{id === '' ? '(vazio)' : id}</code>
            </>
          }
          action={
            <LinkButton to={linkDaLista(location.search)} variant="primary" icon="empresa" keepSearch={false}>
              Ver empresas
            </LinkButton>
          }
        />
      </section>
    );
  }

  return <DetalheCarregado key={conhecida.id} company={conhecida} />;
}

interface ContatoLocal {
  readonly contatoNome: string;
  readonly contatoEmail: string;
}

function DetalheCarregado({ company }: { readonly company: CompanySummary }): JSX.Element {
  const { selectCompany, updateCompany } = useCompany();
  const { range, selection } = usePeriod();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  const abaDaUrl = params.get(PARAM_ABA);
  const aba: AbaDetalhe = ehAbaDetalhe(abaDaUrl) ? abaDaUrl : 'resumo';
  const [contato, setContato] = useState<ContatoLocal | null>(null);
  const [editando, setEditando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [convidados, setConvidados] = useState<readonly TeamMember[]>([]);
  const convidar = useCallback((novo: TeamMember): void => setConvidados((lista) => [...lista, novo]), []);
  const vendedorasConvidadas = convidados.filter((m) => m.role === 'comercial').length;

  useEffect(() => {
    if (!salvo) {
      return undefined;
    }
    const timer = window.setTimeout(() => setSalvo(false), SALVO_VISIVEL_MS);
    return () => window.clearTimeout(timer);
  }, [salvo]);

  const buscar = useCallback((signal: AbortSignal) => fetchCompanyDetail(company.id, range, signal), [company.id, range]);
  const { state, reload } = useApiResource(buscar);

  // Cadastro vivo do contexto + camada local do contato (que o contexto nao guarda).
  const exibida = useMemo<CompanySummary>(
    () => ({
      ...company,
      contactName: contato === null ? company.contactName : contato.contatoNome,
      contactEmail: contato === null ? company.contactEmail : contato.contatoEmail,
      logoInitials: company.logoUrl === null ? iniciaisDe(company.name) : company.logoInitials,
    }),
    [company, contato],
  );

  // Identidade estavel: o Modal recarrega o formulario quando `base` muda.
  const base = useMemo<EdicaoBasica>(
    () => ({ nome: exibida.name, segmento: exibida.segment, status: exibida.status, contatoNome: exibida.contactName, contatoEmail: exibida.contactEmail }),
    [exibida.name, exibida.segment, exibida.status, exibida.contactName, exibida.contactEmail],
  );

  const detalhe = state.status === 'success' ? state.data : null;
  const saude = useMemo(
    () =>
      detalhe === null
        ? null
        : diagnosticar(exibida, { ...detalhe.setup, salesReps: detalhe.setup.salesReps + vendedorasConvidadas }, detalhe.metrics, detalhe.previous, agoraDaTela()),
    [detalhe, exibida, vendedorasConvidadas],
  );

  const trocarAba = useCallback(
    (proxima: AbaDetalhe): void => {
      setParams(
        (anteriores) => {
          const p = new URLSearchParams(anteriores);
          if (proxima === 'resumo') {
            p.delete(PARAM_ABA);
          } else {
            p.set(PARAM_ABA, proxima);
          }
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const irPara = useCallback(
    (proxima: AbaDetalhe): void => {
      trocarAba(proxima);
      window.requestAnimationFrame(() => {
        document.getElementById('abas-da-empresa')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    [trocarAba],
  );

  const abrirCrm = (): void => {
    selectCompany(company.id);
    navigate({ pathname: '/', search: semConsulta(location.search) });
  };

  const salvarEdicao = (nova: EdicaoBasica): void => {
    setContato({ contatoNome: nova.contatoNome, contatoEmail: nova.contatoEmail });
    setEditando(false);
    // Em modo real e PATCH: o "Salvo" so aparece quando o servidor confirmou
    // (se falhar, o contexto desfaz e mostra o aviso).
    void updateCompany(company.id, { name: nova.nome, segment: nova.segmento, status: nova.status }).then((ok) => {
      if (ok) {
        setSalvo(true);
      }
    });
  };

  const fecharEdicao = useCallback((): void => setEditando(false), []);

  const pendenciasIntegracao = saude === null ? 0 : saude.achados.filter((a) => a.aba === 'integracoes' && (a.tom === 'critical' || a.tom === 'warning')).length;
  const abas: readonly TabItem<AbaDetalhe>[] = ABAS_DETALHE.map((a) => {
    if (detalhe === null) {
      return a;
    }
    switch (a.value) {
      case 'ofertas':
        return { ...a, count: formatCount(detalhe.offers.length) };
      case 'equipe':
        return { ...a, count: formatCount(detalhe.team.length + convidados.length) };
      case 'integracoes':
        return pendenciasIntegracao > 0 ? { ...a, count: `${formatCount(pendenciasIntegracao)} ${pendenciasIntegracao === 1 ? 'pendência' : 'pendências'}` } : a;
      default:
        return a;
    }
  });

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <CabecalhoEmpresa
        company={exibida}
        periodo={describeSelection(selection)}
        salvo={salvo}
        saude={saude}
        voltarPara={linkDaLista(location.search)}
        onAbrirCrm={abrirCrm}
        onEditar={() => setEditando(true)}
      />

      {state.status === 'loading' ? <Esqueleto /> : null}

      {state.status === 'error' ? <ErrorPanel title={`Não foi possível carregar os dados de ${exibida.name}`} error={state.error} onRetry={reload} /> : null}

      {state.status === 'success' && state.data === null ? (
        <EmptyState
          title="Sem dados para esta empresa"
          description="A empresa existe no cadastro, mas o servidor não devolveu o detalhe dela. Tente de novo em instantes."
          action={
            <LinkButton to={linkDaLista(location.search)} variant="secondary" icon="seta-esq" keepSearch={false}>
              Voltar para a lista
            </LinkButton>
          }
        />
      ) : null}

      {state.status === 'success' && state.data !== null && saude !== null ? (
        <>
          {state.simulated ? <SimulatedNotice>Retorno, integrações, equipe e histórico desta empresa vêm do catálogo fictício da prévia.</SimulatedNotice> : null}

          <div id="abas-da-empresa" className="flex scroll-mt-4 flex-wrap items-center justify-between gap-3">
            <Tabs items={abas} value={aba} onChange={trocarAba} label="Seções da empresa" panelId={painelId(aba)} />
            <RefreshingDot active={state.refreshing} />
          </div>

          <div id={painelId('resumo')} role="tabpanel" aria-label="Resumo" hidden={aba !== 'resumo'}>
            <AbaResumo detail={state.data} company={exibida} saude={saude} onIrPara={irPara} />
          </div>
          <div id={painelId('ofertas')} role="tabpanel" aria-label="Ofertas" hidden={aba !== 'ofertas'}>
            <AbaOfertas offers={state.data.offers} companyName={exibida.name} expiradosLigado={exibida.funnels.includes('expirados')} />
          </div>
          <div id={painelId('integracoes')} role="tabpanel" aria-label="Integrações" hidden={aba !== 'integracoes'}>
            <PainelIntegracoes integrations={state.data.integrations} />
          </div>
          <div id={painelId('equipe')} role="tabpanel" aria-label="Equipe" hidden={aba !== 'equipe'}>
            <AbaEquipe base={state.data.team} convidados={convidados} onConvidar={convidar} companyId={company.id} companyName={exibida.name} />
          </div>
          <div id={painelId('comissao')} role="tabpanel" aria-label="Comissão e cobrança" hidden={aba !== 'comissao'}>
            <AbaComissao detail={state.data} />
          </div>
          <div id={painelId('historico')} role="tabpanel" aria-label="Histórico" hidden={aba !== 'historico'}>
            <AbaHistorico history={state.data.history} companyName={exibida.name} />
          </div>

          <p className="text-xs text-ink-subtle">
            Período de {formatDate(state.data.periodStart)} a {formatDate(state.data.periodEnd)} · {exibida.name}
            {state.data.previousPeriod === null
              ? ' · sem período anterior para comparar'
              : ` · comparado com ${formatDate(state.data.previousPeriod.start)} a ${formatDate(state.data.previousPeriod.end)}`}
          </p>
        </>
      ) : null}

      <ModalEditarEmpresa open={editando} base={base} onClose={fecharEdicao} onSave={salvarEdicao} />
    </section>
  );
}
